import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoCompleteComponent } from "@syncfusion/ej2-react-dropdowns";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { get } from "lodash";
import useUtils from "services/utils/useUtils";

interface IInputProps {
  name: string;
  required?: string;
  disabled?: boolean;
  xl?: number;
  md?: number;
  col?: number;
  contingenciaId?: 8 | 9 | null;
}

interface CausaRELOption {
  Id: string;
  ContingenciaId: number | null;
  ContingenciaIdRaw: string;
  Codigo: string;
  Descripcion: string;
}

type SyncfusionDataSourceItem = { [key: string]: Object };

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getValueByKeys = (source: Record<string, unknown>, keys: string[]): unknown => {
  const normalized = Object.entries(source).map(([k, v]) => [
    k.replace(/[_\s]/g, "").toLowerCase(),
    v,
  ] as const);

  for (const key of keys) {
    const target = key.replace(/[_\s]/g, "").toLowerCase();
    const match = normalized.find(([k]) => k === target);
    if (match) return match[1];
  }

  return undefined;
};

const extractRows = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  if (!response || typeof response !== "object") return [];

  const src = response as Record<string, unknown>;
  const candidates = [
    src.items,
    src.Items,
    src.result,
    src.Result,
    src.data,
    src.Data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
  }

  return [];
};

const normalizeCode = (value: unknown): string => String(value ?? "").trim();

const isDigitsOnly = (value: string): boolean => /^[0-9]+$/.test(value);

const areCodesEquivalent = (left: unknown, right: unknown): boolean => {
  const a = normalizeCode(left);
  const b = normalizeCode(right);

  if (!a || !b) return false;
  if (a === b) return true;

  if (isDigitsOnly(a) && isDigitsOnly(b)) {
    return String(Number(a)) === String(Number(b));
  }

  return false;
};

const FormCausasREL: React.FC<IInputProps> = ({
  name,
  required,
  disabled = false,
  xl = 12,
  md = 12,
  col = 12,
  contingenciaId = null,
}) => {
  const idCode = name;
  const idDesc = `${name}Descripcion`;
  const idSelected = `${name}Id`;

  const { getCausasSolicitudREL } = useUtils();
  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [allData, setAllData] = useState<CausaRELOption[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [autoCompleteRefreshKey, setAutoCompleteRefreshKey] = useState(0);
  const autoCompleteRef = useRef<AutoCompleteComponent | null>(null);
  const currentCode = useWatch({ control, name: idCode, defaultValue: getValues(idCode) });
  const currentDesc = useWatch({ control, name: idDesc, defaultValue: getValues(idDesc) });

  const filteredData = useMemo(() => {
    if (!allData.length) return [];

    if (contingenciaId === null || contingenciaId === undefined) {
      const filtered = allData.filter(
        (item) =>
          item.ContingenciaId === 8 ||
          item.ContingenciaId === 9 ||
          item.ContingenciaIdRaw.trim() === "8" ||
          item.ContingenciaIdRaw.trim() === "9"
      );
      return filtered.length ? filtered : allData;
    }

    const expected = String(contingenciaId).trim();
    const filtered = allData.filter(
      (item) =>
        item.ContingenciaId === Number(contingenciaId) || item.ContingenciaIdRaw.trim() === expected
    );

    return filtered.length ? filtered : allData;
  }, [allData, contingenciaId]);

  useEffect(() => {
    register(idCode);
    register(idSelected);
  }, [idCode, idSelected, register]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await getCausasSolicitudREL();
        const rows = extractRows(res);
        const mappedData: CausaRELOption[] = rows
          .map((row) => {
            const rawId = getValueByKeys(row, ["id", "Id"]);
            const rawContingenciaId = getValueByKeys(row, [
              "contingenciaId",
              "ContingenciaId",
              "contingencia_id",
            ]);
            const rawCodigo = getValueByKeys(row, ["codigo", "Codigo", "code"]);
            const rawDescripcion = getValueByKeys(row, [
              "descripcion",
              "Descripcion",
              "description",
            ]);

            return {
              Id: String(rawId ?? ""),
              ContingenciaId: toNullableNumber(rawContingenciaId),
              ContingenciaIdRaw: String(rawContingenciaId ?? ""),
              Codigo: String(rawCodigo ?? ""),
              Descripcion: String(rawDescripcion ?? ""),
            };
          })
          .filter((item) => item.Codigo !== "" && item.Descripcion !== "");

        if (isMounted) {
          setAllData(mappedData);
          setCatalogLoaded(true);
        }
      } catch {
        if (isMounted) {
          setAllData([]);
          setCatalogLoaded(true);
        }
      } finally {
        if (isMounted) {
          setCatalogLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!catalogLoaded) {
      return;
    }

    const sourceData = filteredData.length ? filteredData : allData;
    const codeFromForm = normalizeCode(currentCode ?? getValues(idCode));
    const descFromForm = String(currentDesc ?? getValues(idDesc) ?? "");

    if (!sourceData.length) {
      if (!codeFromForm && !descFromForm) {
        setValue(idSelected, "");
      }
      return;
    }

    if (codeFromForm) {
      const selectedByCode = sourceData.find((item) => areCodesEquivalent(item.Codigo, codeFromForm));
      if (selectedByCode) {
        if (descFromForm !== selectedByCode.Descripcion) {
          setValue(idDesc, selectedByCode.Descripcion);
          // Force Syncfusion AutoComplete to re-read controlled value after reset/hydration.
          setAutoCompleteRefreshKey((prev) => prev + 1);
        }
        if (!areCodesEquivalent(codeFromForm, selectedByCode.Codigo)) {
          setValue(idCode, selectedByCode.Codigo);
        }
        setValue(idSelected, selectedByCode.Id);
        return;
      }
    }

    if (descFromForm) {
      const selectedByDesc = sourceData.find((item) => item.Descripcion === descFromForm);
      if (selectedByDesc) {
        if (!areCodesEquivalent(codeFromForm, selectedByDesc.Codigo)) {
          setValue(idCode, selectedByDesc.Codigo);
        }
        setValue(idSelected, selectedByDesc.Id);
        return;
      }
    }

    if (!codeFromForm && !descFromForm) {
      setValue(idSelected, "");
    }
  }, [catalogLoaded, filteredData, allData, currentCode, currentDesc, idCode, idDesc, idSelected, getValues, setValue]);

  const errorMessage = get(errors, idDesc)?.message as string | undefined;

  const openPopupOnFocus = (component: AutoCompleteComponent | null) => {
    if (disabled || catalogLoading || !component || filteredData.length === 0) return;
    component.showPopup();
  };

  const validateSelectedCause = (value: unknown): true | string => {
    const description = String(value ?? "").trim();

    if (!description || !catalogLoaded) return true;

    const sourceData = filteredData.length ? filteredData : allData;
    const selectedCause = sourceData.find((item) => item.Descripcion.trim() === description);

    return selectedCause ? true : "Debe seleccionar una causa válida";
  };

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <fieldset className={`input-block input-block--code ${errorMessage ? "error" : ""}`}>
        <div className="container-leyend">
          <legend className="input-block--code__legend">Causa de la Solicitud</legend>
        </div>

        <div className="row">
          <div
            className="col-12 delta-autocomplete"
            data-input-name={idDesc}
            aria-busy={catalogLoading}
            onMouseDown={() => {
              openPopupOnFocus(autoCompleteRef.current);
            }}
          >
            <Controller
              name={idDesc}
              control={control}
              defaultValue={getValues(idDesc) || ""}
              rules={{ required, validate: validateSelectedCause }}
              render={({ field }) => (
                <AutoCompleteComponent
                  ref={(instance) => {
                    autoCompleteRef.current = instance;
                  }}
                  key={`causa-rel-${autoCompleteRefreshKey}`}
                  id={idDesc}
                  dataSource={filteredData as unknown as SyncfusionDataSourceItem[]}
                  fields={{ text: "Descripcion", value: "Descripcion" }}
                  placeholder={catalogLoading ? "Cargando Datos..." : "Introduzca texto para buscar la causa"}
                  value={String(currentDesc ?? field.value ?? "")}
                  change={(e) => {
                    const selectedDesc = e?.value as string;
                    const selectedItem =
                      (e?.itemData as CausaRELOption | undefined) ||
                      filteredData.find((item) => item.Descripcion === selectedDesc);

                    if (selectedItem) {
                      setValue(idCode, selectedItem.Codigo);
                      setValue(idSelected, selectedItem.Id);
                      field.onChange(selectedItem.Descripcion);
                      return;
                    }

                    setValue(idCode, "");
                    setValue(idSelected, "");
                    field.onChange(selectedDesc || "");
                  }}
                  onBlur={field.onBlur}
                  enabled={!disabled && !catalogLoading}
                  ignoreCase={true}
                  ignoreAccent={true}
                  filterType="Contains"
                  minLength={0}
                  focus={() => {
                    openPopupOnFocus(autoCompleteRef.current);
                  }}
                />
              )}
            />
          </div>
        </div>

        <input type="hidden" {...register(idCode)} />
        <input type="hidden" {...register(idSelected)} />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}
      </fieldset>
    </div>
  );
};

export default FormCausasREL;
